/**
 * similar to ags-geoquery-locator but this actually uses a GeocodeServer to find the address
 * product uses backbone fetch and jquery ajax, fetch API not available w/out ie11 polyfill so using jquery
 */

import Base = require("./base/geoquery");
import Utils = require("../utils");
const log = Utils.log;
import KeywordValidator = require("./base/keywords");
import Converter = require("../utils/ags/converter");

import ol = require("ol");
import geo = require("../utils/geo");
const defaultAgsFindAddressCandidatesService =
  "https://geocode.arcgis.com/arcgis/rest/services/World/GeocodeServer/findAddressCandidates";
const defaultAgsTokenService = "https://www.arcgis.com/sharing/oauth2/token";

// for documentation only
type OutFields = Array<
  | "AddBldg"
  | "AddNum"
  | "AddNumFrom"
  | "AddNumTo"
  | "AddRange"
  | "Addr_type"
  | "address"
  | "BldgName"
  | "BldgType"
  | "Block"
  | "City"
  | "Country"
  | "DisplayX"
  | "DisplayY"
  | "Distance"
  | "District"
  | "ExInfo"
  | "LangCode"
  | "LevelName"
  | "LevelType"
  | "Loc_name"
  | "LongLabel"
  | "Match_addr"
  | "MetroArea"
  | "Nbrhd"
  | "Phone"
  | "PlaceName"
  | "Place_addr"
  | "Postal"
  | "PostalExt"
  | "Rank"
  | "Region"
  | "RegionAbbr"
  | "Score"
  | "Sector"
  | "ShortLabel"
  | "Side"
  | "StAddr"
  | "StDir"
  | "StName"
  | "StPreDir"
  | "StPreType"
  | "StType"
  | "Status"
  | "SubAddr"
  | "Subregion"
  | "Territory"
  | "Type"
  | "URL"
  | "UnitName"
  | "UnitType"
  | "X"
  | "Xmax"
  | "Xmin"
  | "Y"
  | "Ymax"
  | "Ymin"
  | "Zone"
  | "id"
>;

function extentAsPolygon(
  xmin: number,
  ymin: number,
  xmax: number,
  ymax: number
) {
  return ol.geom.Polygon.fromExtent([xmin, ymin, xmax, ymax]);
}

interface Dictionary<T> {
  [index: string]: T;
}

type IAgsRestError = {
  error: {
    code: number;
    extendedCode: number;
    message: string;
    details: Array<string>;
  };
};

// would like to make this available in the response but message bus approach makes this impossible?
// all locator functions (find, query, geocode) should return same models?
type IAddressCandidatesResponse = {
  spatialReference: {
    wkid: number;
    latestWkid: number;
  };
  candidates: Array<{
    address: string;
    attributes: Dictionary<string>;
    extent: {
      xmin: number;
      ymin: number;
      xmax: number;
      ymax: number;
    };
    location: {
      x: number;
      y: number;
    };
    score: number;
  }>;
};

function asEpsgCode(spatialReference: any) {
  return `EPSG:${spatialReference.latestWkid || spatialReference.wkid}`;
}

// if service fails with 499 token might be out-of-date so fetch another own
async function getToken(options: {
  tokenServiceUrl: string;
  appId: string;
  appSecret: string;
}) {
  // move these into configuration, ideally we'd have a server-side solution for this
  let url = `${options.tokenServiceUrl}?client_id=${options.appId}&grant_type=client_credentials&client_secret=${options.appSecret}&f=pjson`;
  let result = await $.ajax({
    url: url,
    type: "POST",
    dataType: "json",
    contentType: "application/json",
    cache: false
  });
  return result.access_token;
}

// default parameters needed by the AGS service
const FindAddressCandidatesDefaultOptions = {
  countryCode: "USA",
  outFields: "LongLabel",
  maxLocations: "1",
  searchExtent: "",
  locationType: "rooftop"
};

/**
 * Command for invoking the AGS geoquery service and putting result on the message bus
 */
class Command extends Base<any> {
  // default parameters needed by the AGS service
  static options = Utils.defaults(
    {
      findAddressCandidatesUrl: defaultAgsFindAddressCandidatesService,
      tokenServiceUrl: defaultAgsTokenService,
      singleLineParameterName: "SingleLine"
    },
    Base.options
  );

  execute(args = this.args) {
    // if no keywords match then skip execution
    if (!KeywordValidator.validate(this)) return;

    // if nothing to search for then skip execution
    const location = this.findOption("location");
    if (!location) {
      log("could into find location");
      return;
    }

    // get the service parameters
    const url =
      this.options.findAddressCandidatesUrl ||
      Command.options.findAddressCandidatesUrl;
    const singleLineParameterName =
      this.options.singleLineParameterName ||
      Command.options.singleLineParameterName;

    const searchExtent = this.mediator.config.Map.Viewport.extent;
    const searchLocation = this.mediator.config.Map.Options.Values.find(
      v => v.id === "init-center"
    );
    const serviceOptions = Utils.defaults(FindAddressCandidatesDefaultOptions, {
      f: "json",
      [singleLineParameterName]: location,
      location: (searchLocation && searchLocation.value) || "",
      searchExtent: searchExtent || ""
    });
    Object.keys(serviceOptions).forEach(
      k => (serviceOptions[k] = this.options[k] || serviceOptions[k])
    );

    const searchArgs = {
      url,
      type: "GET",
      data: serviceOptions
    };

    searchArgs.data;

    const search = async () => {
      const searchResults:
        | IAddressCandidatesResponse
        | IAgsRestError = await $.ajax(searchArgs);
      const error = (searchResults as IAgsRestError).error;
      if (error) {
        switch (error.code) {
          case 498: // token expired
          case 499: // token required
            if (!!searchArgs["token"]) break;
            const appId = this.findOption("app-id");
            const appSecret = this.findOption("app-secret");
            const tokenServiceUrl =
              this.findOption("token-service-url") ||
              Command.options.tokenServiceUrl;
            const token = await getToken({ tokenServiceUrl, appId, appSecret });
            if (!token) break;
            searchArgs["token"] = token;
            search(); // try again with token
            return;
        }
        log("alert", {
          message: `${error.code}: ${error.message}`,
          type: "error"
        });
        return;
      }

      // share the results as "layers" to be consistent with other search services
      // the results have to be such that app/commands/render-features can render them
      const result = searchResults as IAddressCandidatesResponse;
      const srcSrs = asEpsgCode(result.spatialReference);
      const converter = new Converter(srcSrs, "EPSG:4326");
      const layer = <Rhythm.GIS.FeatureQueryResultLayer>{
        spatialReference: {
          wkid: <any>"EPSG:4326"
        },
        features: []
      };

      layer.features = result.candidates.map((v, i) => {
        const { x, y } = v.location;
        const [locationInMeters] = geo.transform("EPSG:4326", "EPSG:3857", [
          [x, y]
        ]);
        const deltaInMeters = parseFloat(
          this.findOption("buffer-size") || "10"
        ); // meters
        const [xmin, ymin] = [
          locationInMeters[0] - deltaInMeters,
          locationInMeters[1] - deltaInMeters
        ];
        const [xmax, ymax] = [
          locationInMeters[0] + deltaInMeters,
          locationInMeters[1] + deltaInMeters
        ];
        const [[west, south], [east, north]] = geo.transform(
          "EPSG:3857",
          "EPSG:4326",
          [
            [xmin, ymin],
            [xmax, ymax]
          ]
        );
        const polygon = extentAsPolygon(west, south, east, north);
        return {
          geometryType: "polygon",
          geometry: <any>polygon.getCoordinates(),
          attributes: {
            ...v.attributes,
            address: v.address,
            id: `address_candidate_${i}`
          }
        };
      });

      layer.displayFieldName = "address";

      this.trigger({
        filterInfo: {},
        serviceInfo: {},
        layers: [layer]
      });
    };

    // perform the search (may be called a second time if the token is bad)
    search();
  }
}

export = Command;
